<template>
  <button
    :id="id"
    ref="trigger"
    v-bind="$attrs"
    type="button"
    role="combobox"
    class="modal-select-trigger"
    data-modal-select-trigger
    :disabled="disabled"
    :aria-required="required"
    :aria-expanded="isOpen"
    :aria-controls="isOpen ? dialogId : undefined"
    aria-haspopup="dialog"
    :title="selectedEntry?.label"
    @click="open"
    @keydown.down.prevent="open"
  >
    <span :class="{ 'modal-select-placeholder': !selectedEntry }">
      {{ selectedEntry ? selectedEntry.label : placeholder }}
    </span>
    <svg aria-hidden="true" viewBox="0 0 20 20" fill="none">
      <path d="m5 7.5 5 5 5-5" stroke="currentColor" stroke-width="1.5" />
    </svg>
  </button>
  <Teleport to="body">
    <dialog
      v-if="isOpen"
      :id="dialogId"
      ref="dialog"
      class="modal-select-dialog"
      :style="{ fontFamily: fieldFontFamily }"
      :aria-labelledby="`${dialogId}-title`"
      aria-modal="true"
      @cancel.prevent="close"
      @keydown="handleDialogKeydown"
    >
      <header class="modal-select-header">
        <h2 :id="`${dialogId}-title`">{{ title }}</h2>
        <button
          type="button"
          class="modal-select-close"
          aria-label="Fechar seletor"
          data-modal-select-close
          @click="close"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
            <path d="m6 6 12 12M6 18 18 6" stroke="currentColor" stroke-width="2" />
          </svg>
        </button>
      </header>
      <div v-if="showSearch" class="modal-select-search">
        <label :for="`${dialogId}-search`" class="form-field-label">Pesquisar opções</label>
        <input
          :id="`${dialogId}-search`"
          ref="searchInput"
          v-model="search"
          type="search"
          class="field"
          placeholder="Digite para pesquisar"
          autocomplete="off"
          @keydown.down.prevent="focusFirstOption"
        />
        <p class="modal-select-count" role="status" aria-live="polite">
          {{ filteredEntries.length }} {{ filteredEntries.length === 1 ? 'opção disponível' : 'opções disponíveis' }}
        </p>
      </div>
      <div class="modal-select-options" :class="{ 'modal-select-options-without-search': !showSearch }">
        <fieldset v-if="filteredEntries.length">
          <legend class="modal-select-sr-only">{{ title }}</legend>
          <label
            v-for="(entry, index) in filteredEntries"
            :key="entry.index"
            class="modal-select-option"
            :class="{ 'modal-select-option-selected': isDraft(entry) }"
            @click.prevent="select(entry)"
          >
            <input
              type="radio"
              :name="dialogId"
              :checked="isDraft(entry)"
              :value="entry.index"
              @keydown="handleOptionKeydown($event, index)"
            />
            <span>{{ entry.label }}</span>
          </label>
        </fieldset>
        <p v-else class="modal-select-empty">
          {{ availableEntries.length ? 'Nenhuma opção encontrada.' : 'Nenhuma opção disponível.' }}
        </p>
      </div>
      <footer class="modal-select-footer">
        <button type="button" class="btn-secondary" data-modal-select-cancel @click="close">
          Cancelar
        </button>
      </footer>
    </dialog>
  </Teleport>
</template>

<script>
import { getCurrentInstance } from "vue";

const isObject = (value) => value !== null && typeof value === "object";
const normalize = (value) => String(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("pt-BR");

export default {
  name: "ModalSelect",
  inheritAttrs: false,
  props: {
    modelValue: { default: null },
    options: { type: Array, default: () => [] },
    valueKey: { type: [String, Function], default: "id" },
    labelKey: { type: String, default: "descricao" },
    // Declare true for object models that initially contain null.
    // Otherwise infer the contract from an existing object model.
    returnObject: { type: Boolean, default: undefined },
    customLabel: { type: Function, default: null },
    placeholder: { type: String, default: "Selecione uma opção" },
    title: { type: String, default: "Selecionar opção" },
    id: { type: String, default: undefined },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "change"],
  setup() {
    return { dialogId: `modal-select-${getCurrentInstance().uid}` };
  },
  data() {
    return { isOpen: false, search: "", draftValue: null, hasDraft: false, previousOverflow: "", fieldFontFamily: "" };
  },
  computed: {
    entries() {
      return this.options.map((option, index) => ({
        option,
        index,
        value: this.optionValue(option),
        label: String(this.customLabel ? this.customLabel(option) : isObject(option) ? option[this.labelKey] ?? "" : option),
      }));
    },
    objectModel() {
      return this.returnObject ?? isObject(this.modelValue);
    },
    selectedEntry() {
      const value = this.optionValue(this.modelValue);
      const exact = this.entries.find((entry) => Object.is(entry.value, value));
      if (exact) return exact;
      // Native selects accepted numeric IDs persisted as strings. Match these
      // for display only; confirmation always emits the original option value.
      return this.entries.find((entry) =>
        [typeof entry.value, typeof value].every((type) => type === "string" || type === "number") &&
        String(entry.value) === String(value),
      ) || null;
    },
    availableEntries() {
      return this.entries.filter((entry) => !this.required || normalize(entry.label.trim()) !== "selecione");
    },
    showSearch() {
      return this.availableEntries.length >= 5;
    },
    filteredEntries() {
      const query = normalize(this.showSearch ? this.search.trim() : "");
      return this.availableEntries.filter((entry) => normalize(entry.label).includes(query));
    },
    draftEntry() {
      return this.hasDraft ? this.entries.find((entry) => Object.is(entry.value, this.draftValue)) || null : null;
    },
  },
  watch: {
    disabled(value) {
      if (value && this.isOpen) this.close();
    },
  },
  beforeUnmount() {
    if (this.isOpen) document.body.style.overflow = this.previousOverflow;
  },
  methods: {
    optionValue(option) {
      if (!isObject(option)) return option;
      return typeof this.valueKey === "function" ? this.valueKey(option) : option[this.valueKey];
    },
    async open() {
      if (this.disabled || this.isOpen) return;
      this.search = "";
      this.hasDraft = Boolean(this.selectedEntry);
      this.draftValue = this.selectedEntry?.value;
      this.fieldFontFamily = window.getComputedStyle(this.$refs.trigger).fontFamily;
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      this.isOpen = true;
      await this.$nextTick();
      if (!this.isOpen || !this.$refs.dialog) return;
      this.$refs.dialog.showModal();
      if (this.$refs.searchInput) this.$refs.searchInput.focus();
      else this.focusFirstOption();
    },
    async close() {
      if (!this.isOpen) return;
      this.$refs.dialog?.close();
      this.isOpen = false;
      document.body.style.overflow = this.previousOverflow;
      await this.$nextTick();
      this.$refs.trigger?.focus();
    },
    choose(entry) {
      this.draftValue = entry.value;
      this.hasDraft = true;
    },
    isDraft(entry) {
      return this.hasDraft && Object.is(entry.value, this.draftValue);
    },
    select(entry) {
      if (!this.isOpen || this.disabled) return;
      this.choose(entry);
      this.confirm();
    },
    confirm() {
      if (this.disabled || !this.draftEntry) return;
      const entry = this.draftEntry;
      if (!this.selectedEntry || !Object.is(this.selectedEntry.value, entry.value)) {
        const value = this.objectModel ? entry.option : entry.value;
        this.$emit("update:modelValue", value);
        this.$emit("change", value);
      }
      this.close();
    },
    focusFirstOption() {
      const dialog = this.$refs.dialog;
      const target = dialog.querySelector('input[type="radio"]:checked') ||
        dialog.querySelector('input[type="radio"]') ||
        dialog.querySelector('[data-modal-select-close]');
      target?.focus();
    },
    async handleOptionKeydown(event, index) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.select(this.filteredEntries[index]);
        return;
      }
      const length = this.filteredEntries.length;
      const movements = { ArrowDown: (index + 1) % length, ArrowRight: (index + 1) % length,
        ArrowUp: (index + length - 1) % length, ArrowLeft: (index + length - 1) % length,
        Home: 0, End: length - 1 };
      if (!(event.key in movements)) return;
      event.preventDefault();
      const next = movements[event.key];
      this.choose(this.filteredEntries[next]);
      await this.$nextTick();
      this.$refs.dialog.querySelectorAll('input[type="radio"]')[next]?.focus();
    },
    handleDialogKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
      }
      if (event.key !== "Tab") return;
      const elements = [...this.$refs.dialog.querySelectorAll('button:not(:disabled), input:not(:disabled)')];
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    },
  },
};
</script>

<style scoped>
.modal-select-trigger {
  @apply w-full h-10 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-slate-700 disabled:cursor-not-allowed transition-colors duration-200;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  text-align: left;
  min-width: 0;
}
.modal-select-trigger:hover:not(:disabled) { @apply border-gray-400 dark:border-slate-500; }
.modal-select-trigger > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.modal-select-trigger > svg { width: 1rem; height: 1rem; flex-shrink: 0; }
.modal-select-placeholder { @apply text-gray-500 dark:text-gray-400; }
.modal-select-dialog {
  @apply bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-slate-700;
  width: min(720px, calc(100vw - 2rem));
  max-width: none;
  max-height: calc(100dvh - 2rem);
  margin: auto;
  padding: 0;
  border-radius: 12px;
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.22);
  overflow: hidden;
  color-scheme: light;
}
.modal-select-dialog[open] { display: flex; flex-direction: column; }
.dark .modal-select-dialog { color-scheme: dark; }
.modal-select-dialog::backdrop { background: rgba(15, 23, 42, 0.55); }
.modal-select-header {
  @apply border-b border-gray-200 dark:border-slate-700;
  display: flex; align-items: center; justify-content: space-between; gap: 1rem;
  padding: 1.25rem 1.5rem; flex-shrink: 0;
}
.modal-select-header h2 { margin: 0; font-size: 1.125rem; font-weight: 600; overflow-wrap: anywhere; }
.modal-select-close {
  @apply text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500;
  width: 2.5rem; height: 2.5rem; padding: 0.5rem; flex-shrink: 0;
}
.modal-select-close svg { width: 100%; height: 100%; }
.modal-select-search { padding: 1.25rem 1.5rem 0.75rem; flex-shrink: 0; }
.modal-select-search input { font-size: 1rem; }
.modal-select-count { @apply text-gray-600 dark:text-gray-400; margin: 0.5rem 0 0; font-size: 0.875rem; }
.modal-select-options { min-height: 0; overflow-y: auto; overflow-x: hidden; overscroll-behavior: contain; padding: 0 1.5rem 1.25rem; }
.modal-select-options-without-search { padding-top: 1rem; }
.modal-select-options fieldset { border: 0; padding: 0; margin: 0; min-width: 0; }
.modal-select-option {
  @apply border-b border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700;
  display: flex; align-items: flex-start; gap: 0.75rem;
  padding: 1rem 0.75rem; cursor: pointer;
}
.modal-select-option-selected { @apply bg-blue-50 dark:bg-slate-700; }
.modal-select-option input { flex-shrink: 0; width: 1rem; height: 1rem; margin-top: 0.25rem; accent-color: #2563eb; }
.modal-select-option input:focus-visible { outline: 2px solid #2563eb; outline-offset: 3px; }
.modal-select-option span { min-width: 0; white-space: normal; overflow-wrap: anywhere; line-height: 1.5; }
.modal-select-empty { @apply text-gray-600 dark:text-gray-400; padding: 2rem 0; text-align: center; }
.modal-select-footer {
  @apply border-t border-gray-200 dark:border-slate-700;
  display: flex; justify-content: flex-end; gap: 0.75rem; padding: 1rem 1.5rem; flex-shrink: 0;
}
.modal-select-sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
@media (max-width: 639px) {
  .modal-select-dialog { width: 100%; height: 100dvh; max-height: 100dvh; border-radius: 0; border: 0; }
  .modal-select-header, .modal-select-footer { padding: 1rem; }
  .modal-select-search { padding: 1rem 1rem 0.75rem; }
  .modal-select-options { flex: 1; padding-inline: 1rem; }
  .modal-select-footer { padding-bottom: max(1rem, env(safe-area-inset-bottom)); }
  .modal-select-footer button { flex: 1; }
}
</style>
